import { formatName } from "@api";
import { Autocomplete, type AutocompleteProps, TextField, type TextFieldProps, Typography } from "@mui/material";
import type { ElementType } from "react";
import { type IUseAutocompleteCandidates, type TAutocompleteCandidateValue, useAutocompleteCandidates } from "./logic";

export type TAutocompleteCandidateBaseProps<
  Multiple extends boolean | undefined = undefined,
  ChipComponent extends React.ElementType = "div",
> = Omit<
  AutocompleteProps<TAutocompleteCandidateValue, Multiple, false, false, ChipComponent>,
  "getOptionLabel" | "renderOption" | "renderInput" | "options"
> & {
  TextFieldProps: Omit<TextFieldProps, "value" | "onChange">;
  handleChange: (value: TAutocompleteCandidateValue[] | TAutocompleteCandidateValue | null) => void;
};

export const AutocompleteCandidates = <
  Multiple extends boolean | undefined = undefined,
  ChipComponent extends ElementType = "div",
>({
  value,
  handleChange,
  TextFieldProps,
  ...props
}: TAutocompleteCandidateBaseProps<Multiple, ChipComponent>) => {
  const { options, getDebounceCandidates }: IUseAutocompleteCandidates = useAutocompleteCandidates();

  return (
    <Autocomplete
      {...props}
      value={value}
      onChange={(_, value) => handleChange(value)}
      onInputChange={getDebounceCandidates}
      options={options}
      isOptionEqualToValue={(option, value) => !!option && !!value && option._id === value._id}
      getOptionLabel={(option) => (option ? formatName(option) : option)}
      renderOption={(props, option) =>
        option && (
          <li {...props} key={option._id}>
            <Typography
              sx={{
                pr: 1,
              }}
            >
              {formatName(option)}
            </Typography>
            <Typography variant="caption">• #{option.profileId}</Typography>
          </li>
        )
      }
      renderInput={(params) => <TextField {...params} {...TextFieldProps} />}
      noOptionsText="Pas de résultat. Saisissez au moins 2 caractères pour avoir des propositions de candidats."
      {...(props.multiple && { open: options.length > 0 })}
    />
  );
};
